/** Durable frame visibility (Figma-style layer hide), persisted as `metadata.hidden`. */
import * as spacesApi from "@/api/spaces";
import type { FrameData } from "@/lib/frame-data";
import { changeFrameMetadata } from "@/lib/frame-detail-requests";
import { useSelectionStore } from "@/stores/selection-store";
import { useFramesStore } from "@/stores/frames-store";
import { useProjectStore } from "@/stores/project-store";

/** A hidden or removed terminal frame stops its local PTY session. */
export function stopFrameTerminal(frame: FrameData | undefined): void {
  if (frame?.frameType !== "terminal") return;
  if (typeof window === "undefined" || !("__TAURI_INTERNALS__" in window)) {
    return;
  }
  const sessionId = frame.content?.terminalSessionId;
  if (typeof sessionId !== "string" || !sessionId) return;
  void import("@/desktop/terminal-pty")
    .then(({ terminalStop }) => terminalStop(sessionId))
    .catch(() => {});
}

/** Clear `metadata.hidden` in the frames store only; the caller persists it. */
export function showFramesLocally(frameIds: readonly string[]): void {
  const { framesById, patchFrames } = useFramesStore.getState();
  patchFrames(
    frameIds.flatMap((id) => {
      const frame = framesById[id];
      return frame?.metadata?.hidden
        ? [[id, { metadata: { ...frame.metadata, hidden: false } }] as const]
        : [];
    }),
  );
}

/**
 * The one writer of durable visibility: updates `metadata.hidden` in the
 * frames store at once (the canvas node follows) and persists the merged
 * metadata (`changeFrameMetadata`). Hiding the selected frame clears the
 * selection.
 */
export function setFrameHidden(frameId: string, hidden: boolean): void {
  const frame = useFramesStore.getState().framesById[frameId];
  if (!frame) return;
  if (hidden) stopFrameTerminal(frame);
  changeFrameMetadata(
    frameId,
    (metadata) => ({ ...metadata, hidden }),
    (metadata) => {
      const { currentProject, currentSpace } = useProjectStore.getState();
      if (!currentProject || !currentSpace) return;
      void spacesApi
        .updateFrame(currentProject.id, currentSpace.id, frameId, { metadata })
        .catch((err) =>
          console.error("[frame-visibility] Backend update failed:", err),
        );
    },
  );
  if (hidden) useSelectionStore.getState().retain((id) => id !== frameId);
}
